import { useEffect, useMemo, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import toast, { Toaster } from "react-hot-toast";

import SearchBox from "../SearchBox/SearchBox";
import Pagination from "../Pagination/Pagination";
import NoteList from "../NoteList/NoteList";
import Modal from "../Modal/Modal";
import NoteForm from "../NoteForm/NoteForm";
import Loader from "../Loader/Loader";
import ErrorMessage from "../ErrorMessage/ErrorMessage";

import { deleteNote, fetchNotes } from "../../services/noteService";
import type { Note } from "../../types/note";
import css from "./App.module.css";

const PER_PAGE = 12;

export default function App() {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const notesQuery = useQuery({
    queryKey: ["notes", page, PER_PAGE, debouncedSearch],
    queryFn: () =>
      fetchNotes({
        page,
        perPage: PER_PAGE,
        search: debouncedSearch,
      }),
    placeholderData: keepPreviousData,
  });

  const notes: Note[] = useMemo(
    () => notesQuery.data?.notes ?? [],
    [notesQuery.data]
  );
  const totalPages = notesQuery.data?.totalPages ?? 0;

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => deleteNote(noteId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["notes"] });
      toast.success("Note deleted");
    },
    onError: () => {
      toast.error("Failed to delete note");
    },
  });

  function handleDelete(noteId: string) {
    deleteMutation.mutate(noteId);
  }

  return (
    <div className={css.app}>
      <header className={css.toolbar}>
        <SearchBox value={search} onChange={setSearch} />

        {totalPages > 1 && (
          <Pagination
            pageCount={totalPages}
            currentPage={page}
            onPageChange={setPage}
          />
        )}

        <button
          className={css.button}
          type="button"
          onClick={() => setIsCreateOpen(true)}
        >
          Create note +
        </button>
      </header>

      {(notesQuery.isLoading ||
        (notesQuery.isFetching && !notesQuery.isError)) && <Loader />}
      {notesQuery.isError && <ErrorMessage />}

      {notes.length > 0 && <NoteList notes={notes} onDelete={handleDelete} />}

      {isCreateOpen && (
        <Modal onClose={() => setIsCreateOpen(false)}>
          <NoteForm onCancel={() => setIsCreateOpen(false)} />
        </Modal>
      )}

      <Toaster />
    </div>
  );
}
